import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
  useMemo
} from 'react';
import type { Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { useChatUIContext } from './ChatUIContext';
import { supabase } from '../libs/supabaseClient';
import {
  textToSpeech as elevenLabsTextToSpeech,
  isElevenLabsConfigured,
  streamingTTS
} from '../services/elevenLabsService';
import { ARGuideEventData, ARGuide } from '../types/arGuides';
import { useARGuide } from './ARGuideContext';
import ChatService from '../services/chat/chatService';
import { supportSocket } from '../services/chat/supportSocket';
import { SupportStatus } from '../types/chat';
import {
  ChatConversation,
  CONVERSATIONS_STORAGE_KEY,
  LEGACY_MESSAGES_KEY,
  buildConversationTitle,
  createConversationId,
  createSessionId,
  addConversationWithEviction,
  migrateLegacyMessages,
  splitPendingIntoBatches,
  buildOfflineTurnMeta,
  mergeBatchContents,
} from '../helpers/chatQueueHelper';
import { useOffline } from './OfflineContext';
import { voiceNoteStore } from '../services/chat/voiceNoteStore';
import type { RecordedVoiceNote } from '../hooks/useVoiceNoteRecorder';
import {
  findOrphanVoiceNoteIds,
  isUntranscribedVoiceNote,
  VOICE_NOTE_CONVERSATION_TITLE,
} from '../helpers/voiceNoteHelper';

// Tipos de mensajes en el chat
export type ChatMessageRole = 'user' | 'assistant' | 'system' | 'agent';

export interface ChatMessage {
  id: string;
  role: ChatMessageRole;
  content: string;
  timestamp: string;
  isStreaming?: boolean;
  actions?: import('../types/chat').ChatAction[];
  traceId?: string;
  feedbackThumb?: boolean | null;
  feedbackText?: string | null;
  /** Estado de envío del mensaje del usuario (cola offline, KAN-218). Ausente = enviado. */
  status?: 'pendiente' | 'enviado' | 'error';
  /** Nota de voz grabada sin conexión (KAN-225): content vacío hasta que se transcribe al reconectar. */
  voiceNote?: { durationMs: number; transcribing?: boolean; transcriptionTraceId?: string };
  /** Escrito/grabado sin conexión: el turno que lo envía se marca offline en el admin del bot (KAN-225). */
  queuedOffline?: boolean;
}

export type TTSMethod = 'native' | 'elevenlabs';

export interface BudgetWarning {
  message: string;
  percentage: number;
  remaining: number;
  limit: number;
}

export interface ChatState {
  messages: ChatMessage[];
  /** Conversaciones del usuario (máx 4 persistidas, KAN-218). */
  conversations: ChatConversation[];
  activeConversationId: string | null;
  activeConversation: ChatConversation | null;
  isOpen: boolean;
  isStreaming: boolean;
  isConnected: boolean;
  unreadCount: number;
  /** Mensajes user pendientes de envío en todas las conversaciones (cola offline). */
  pendingCount: number;
  error: string | null;
  isEnabled: boolean;
  autoSpeakEnabled: boolean;
  autoSpeakActivatedAt: number | null;
  speakingMessageId: string | null;
  ttsMethod: TTSMethod;
  budgetWarning: BudgetWarning | null;
  budgetLimitExceeded: boolean;
  // Slice soporte (handoff a humano)
  supportStatus: SupportStatus;
  supportId: string | null;
  supportAdminTyping: boolean;
}

export interface ChatActions {
  sendMessage: (content: string) => Promise<void>;
  /** Nota de voz grabada sin conexión: queda en cola y se transcribe al reconectar (KAN-225). */
  sendVoiceNote: (note: RecordedVoiceNote) => Promise<void>;
  /** Descarta una nota de voz todavía no transcripta (mensaje + audio local). */
  discardVoiceNote: (messageId: string) => Promise<void>;
  clearChat: () => void;
  /** Nueva conversación (la activa pasa a ser una vacía, no persistida hasta el primer mensaje). */
  createConversation: () => void;
  selectConversation: (id: string) => void;
  closeConversation: (id: string) => void;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  retryLastMessage: () => Promise<void>;
  setError: (error: string | null) => void;
  toggleAutoSpeak: () => void;
  speakMessage: (messageId: string, content: string) => void;
  cancelSpeech: () => void;
  setTTSMethod: (method: TTSMethod) => void;
  clearBudgetWarning: () => void;
  submitFeedback: (messageId: string, feedbackThumb: boolean, feedbackText: string | null) => Promise<void>;
  /** sessionId de la conversación activa (viaja en el confirm de pending ops para
   *  que el agente sepa que la acción se ejecutó por botón — incidente 2026-10-31). */
  getActiveSessionId: () => string;
  // Slice soporte (handoff a humano)
  requestHandoffCapture: () => void;
  startHandoff: (initialMessage: string, visitorName?: string, visitorEmail?: string) => Promise<void>;
  cancelHandoff: () => void;
  sendSupportMessage: (text: string) => Promise<void>;
  closeHandoff: () => Promise<void>;
}

export type ChatContextType = ChatState & ChatActions;

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
  apiEndpoint?: string;
  storageKey?: string;
}

const DEFAULT_STORAGE_KEY = 'campo_manager_chat_history';
const MAX_STORED_MESSAGES = 100;
// Debounce para mergear mensajes rápidos consecutivos en un turno (spec KAN-218
// §2 — el usuario pidió menos que los 500ms de referencia de la industria).
const DEBOUNCE_MS = 250;

// LocalStorage keys
const CHAT_ENABLED_KEY = 'campo_manager_chat_enabled';
const CHAT_OPEN_KEY = 'campo_manager_chat_open';
const TTS_METHOD_KEY = 'campo_manager_tts_method';
// Los mensajes ahora viven en conversaciones (campo_manager_chat_conversations,
// ver chatQueueHelper). LEGACY_MESSAGES_KEY es el historial único pre-KAN-218.

/**
 * Sonido de notificación para atención al cliente (sin assets: Web Audio API).
 * Suena cuando el equipo te escribe o cuando se conecta a atenderte — es la
 * señal más importante del chat, por eso siempre suena (aunque la ventana esté
 * abierta). Best-effort: si el navegador bloquea el autoplay, silencio total.
 */
function playSupportChime() {
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const play = (freq: number, start: number, dur: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t0 = ctx.currentTime + start;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + dur + 0.05);
    };
    play(880, 0, 0.15);        // A5
    play(1174.66, 0.18, 0.28); // D6
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch {
    /* silencio: nunca romper el flujo por el sonido */
  }
}

// ElevenLabs voice ID específico
const ELEVENLABS_VOICE_ID = 'JBFqnCBsd6RMkjVDRZzb';

export const ChatProvider: React.FC<ChatProviderProps> = ({
  children,
  apiEndpoint = `${process.env.REACT_APP_CHATBOT_URL || 'http://localhost:3005'}/api/chat/message/stream`,
  storageKey = DEFAULT_STORAGE_KEY
}) => {
  const { user } = useAuth();
  const { startGuide } = useARGuide();
  const { isOnline } = useOffline();
  const chatUi = useChatUIContext();

  // Estado del chat — modelo multi-conversación (KAN-218): la lista de
  // conversaciones es la fuente de verdad; `messages` (los consumidores
  // históricos) es un derivado de la conversación activa.
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [autoSpeakEnabled, setAutoSpeakEnabled] = useState(false);
  const [autoSpeakActivatedAt, setAutoSpeakActivatedAt] = useState<number | null>(null);
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  // Por defecto usamos ElevenLabs; si falla (o no hay API key) speakMessage
  // cae automáticamente a la voz nativa del navegador. El botón para cambiar
  // manualmente de método se ocultó (no hace falta togglable).
  const [ttsMethod, setTTSMethod] = useState<TTSMethod>('elevenlabs');
  const [budgetWarning, setBudgetWarning] = useState<BudgetWarning | null>(null);
  const [budgetLimitExceeded, setBudgetLimitExceeded] = useState(false);

  // Slice soporte (handoff a humano)
  const [supportStatus, setSupportStatus] = useState<SupportStatus>('idle');
  const [supportId, setSupportId] = useState<string | null>(null);
  const [supportAdminTyping, setSupportAdminTyping] = useState(false);

  // Refs para thread-safe updates
  const conversationsRef = useRef<ChatConversation[]>([]);
  const activeIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Cola offline (KAN-218)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const drainingRef = useRef(false);
  const stopDrainRef = useRef(false); // budget excedido corta TODOS los drains
  const isOnlineRef = useRef(isOnline);
  isOnlineRef.current = isOnline;
  const ttsMethodRef = useRef<TTSMethod>(ttsMethod);
  ttsMethodRef.current = ttsMethod;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastMessageIdRef = useRef<string>('');
  const lastSpokenLengthRef = useRef<number>(0);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null); // Referencia al audio actual de ElevenLabs
  const supportSocketRef = useRef<Socket | null>(null); // Socket del handoff a humano
  // Marca si ya restauramos el historial de soporte de un supportId dado, para
  // no apilarlo de nuevo sobre messagesRef (localStorage / re-renders / StrictMode).
  const restoredSupportIdRef = useRef<string | null>(null);

  // Inicializar estado desde localStorage
  useEffect(() => {
    try {
      const storedEnabled = localStorage.getItem(CHAT_ENABLED_KEY);
      const storedOpen = localStorage.getItem(CHAT_OPEN_KEY);
      const storedTTSMethod = localStorage.getItem(TTS_METHOD_KEY);

      // Chat enabled: por defecto true si no está guardado
      if (storedEnabled !== null) {
        setIsEnabled(storedEnabled === 'true');
      }

      if (storedOpen !== null) {
        setIsOpen(storedOpen === 'true');
      }

      // Conversaciones persistidas; si no hay, migración one-shot del
      // historial único viejo (spec KAN-218 §4).
      const storedConversations = localStorage.getItem(CONVERSATIONS_STORAGE_KEY);
      let loaded: ChatConversation[] = [];
      if (storedConversations) {
        try {
          const parsed: ChatConversation[] = JSON.parse(storedConversations);
          if (Array.isArray(parsed) && parsed.length > 0) loaded = parsed;
        } catch { /* historial corrupto → empezar de cero */ }
      }
      if (loaded.length === 0) {
        const migrated = migrateLegacyMessages(localStorage.getItem(LEGACY_MESSAGES_KEY));
        if (migrated) loaded = [migrated];
      }
      conversationsRef.current = loaded;
      setConversations(loaded);
      // Notas de voz (KAN-225): audios sin nota pendiente que los use → borrar.
      voiceNoteStore.listIds()
        .then((ids) => voiceNoteStore.removeMany(findOrphanVoiceNoteIds(ids, loaded)))
        .catch(() => { /* best-effort */ });
      if (loaded.length > 0) {
        const last = loaded.reduce((a, b) => (a.updatedAt >= b.updatedAt ? a : b));
        activeIdRef.current = last.id;
        setActiveConversationId(last.id);
      }

      // Cargar preferencias de TTS
      if (storedTTSMethod === 'native' || storedTTSMethod === 'elevenlabs') {
        setTTSMethod(storedTTSMethod);
      }
    } catch (e) {
      console.error('CHAT: Error loading chat state from localStorage:', e);
    }
  }, []);

  // Obtiene (o crea) la conversación activa. Crea lazy con título del primer
  // mensaje — las conversaciones vacías no se persisten (spec KAN-218 §4).
  const ensureActiveConversation = useCallback((firstMessageContent?: string): ChatConversation => {
    const existing = conversationsRef.current.find((c) => c.id === activeIdRef.current);
    if (existing && existing.messages.length > 0) return existing;
    const now = Date.now();
    const conv: ChatConversation = {
      id: createConversationId(),
      sessionId: createSessionId(),
      title: firstMessageContent ? buildConversationTitle(firstMessageContent) : 'Nueva conversación',
      messages: [],
      createdAt: now,
      updatedAt: now,
    };
    const next = addConversationWithEviction(
      existing ? conversationsRef.current.filter((c) => c.id !== existing.id) : conversationsRef.current,
      conv
    );
    // Conversación evictada por el límite de 4: borrar sus audios (KAN-225).
    const nextIds = new Set(next.map((c) => c.id));
    conversationsRef.current
      .filter((c) => !nextIds.has(c.id) && c.id !== existing?.id)
      .forEach((c) => { voiceNoteStore.removeByConversation(c.id).catch(() => {}); });
    conversationsRef.current = next;
    setConversations(next);
    activeIdRef.current = conv.id;
    setActiveConversationId(conv.id);
    return conv;
  }, []);

  // Mensajes de la conversación activa desde refs (para callbacks estables).
  const getActiveMessages = useCallback((): ChatMessage[] => {
    return conversationsRef.current.find((c) => c.id === activeIdRef.current)?.messages ?? [];
  }, []);

  // sessionId de la conversación activa (persistente — KAN-218).
  const getActiveSessionId = useCallback((): string => {
    return conversationsRef.current.find((c) => c.id === activeIdRef.current)?.sessionId || '';
  }, []);

  // Aplica una transformación a los mensajes de la conversación activa, bumpa
  // updatedAt y persiste TODAS las conversaciones (máx 100 mensajes por una).
  // Si no hay conversación activa (outreach de soporte sin historial), crea una.
  const updateActiveMessages = useCallback((fn: (msgs: ChatMessage[]) => ChatMessage[]) => {
    const convId = activeIdRef.current ?? ensureActiveConversation().id;
    const updated = conversationsRef.current.map((c) =>
      c.id === convId
        ? { ...c, messages: fn(c.messages).slice(-MAX_STORED_MESSAGES), updatedAt: Date.now() }
        : c
    );
    conversationsRef.current = updated;
    setConversations(updated);
    try {
      localStorage.setItem(CONVERSATIONS_STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
      console.error('CHAT: Error persisting conversations:', e);
    }
  }, [ensureActiveConversation]);

  // Persistir estado de apertura
  const persistOpenState = useCallback((open: boolean) => {
    try {
      localStorage.setItem(CHAT_OPEN_KEY, String(open));
    } catch (e) {
      console.error('CHAT: Error persisting open state:', e);
    }
  }, []);

  // Mensajes de la conversación activa (derivado — los consumidores no cambian)
  const messages = useMemo(
    () => conversations.find((c) => c.id === activeConversationId)?.messages ?? [],
    [conversations, activeConversationId]
  );
  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeConversationId) ?? null,
    [conversations, activeConversationId]
  );

  // Total de mensajes pendientes de envío (todas las conversaciones) para el
  // banner offline (spec KAN-218 §6).
  const pendingCount = useMemo(
    () => conversations.reduce(
      (acc, c) => acc + c.messages.filter((m) => m.role === 'user' && m.status === 'pendiente').length,
      0
    ),
    [conversations]
  );

  // Calcular mensajes no leídos
  const unreadCount = useMemo(() => {
    return messages.filter(
      msg => (msg.role === 'assistant' || msg.role === 'agent') && !isOpen
    ).length;
  }, [messages, isOpen]);

  // Generar ID único para mensaje
  const generateMessageId = useCallback(() => {
    return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }, []);

  // sessionId: ya no es estado — cada conversación trae el suyo, persistente
  // (ver getActiveSessionId / chatQueueHelper).

  // Cargar voces del navegador (Chrome las carga asíncronamente)
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      const loadVoices = () => {
        window.speechSynthesis.getVoices();
        console.log('[Chat] Voces nativas cargadas:', window.speechSynthesis.getVoices().length);
      };

      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // Slice soporte (handoff a humano)
  // ─────────────────────────────────────────────────────────────────────
  //
  // Helpers para agregar mensajes al stream de chat con rol específico del
  // handoff. Respetan el patrón del provider (messagesRef + setMessages +
  // persistMessages) para que el recovery y los mensajes de admin/usuario
  // convivan sin perder estado.

  const pushUser = useCallback((text: string) => {
    const msg: ChatMessage = {
      id: generateMessageId(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    updateActiveMessages((msgs) => [...msgs, msg]);
  }, [generateMessageId, updateActiveMessages]);

  const pushAgent = useCallback((text: string, ts?: string) => {
    if (!text) return;
    const msg: ChatMessage = {
      id: generateMessageId(),
      role: 'agent',
      content: text,
      timestamp: ts || new Date().toISOString(),
    };
    updateActiveMessages((msgs) => [...msgs, msg]);
  }, [generateMessageId, updateActiveMessages]);

  const pushSystem = useCallback((text: string) => {
    const msg: ChatMessage = {
      id: generateMessageId(),
      role: 'system',
      content: text,
      timestamp: new Date().toISOString(),
    };
    updateActiveMessages((msgs) => [...msgs, msg]);
  }, [generateMessageId, updateActiveMessages]);

  // Resumen corto de los últimos ~3 turnos del chat con la IA para enviar al
  // admin como contexto del handoff. Trunca a 300 chars para no inflar el
  // payload del POST /request. Si no hay historial previo, string vacío.
  const buildContextSummary = useCallback((): string => {
    const recent = getActiveMessages()
      .filter(m => (m.role === 'user' || m.role === 'assistant') && m.content)
      .slice(-6); // últimos ~3 turnos (user+assistant)
    if (recent.length === 0) return '';
    const summary = recent
      .map(m => `${m.role === 'user' ? 'Usuario' : 'Asistente'}: ${m.content}`)
      .join('\n');
    return summary.length > 300 ? summary.slice(-300) : summary;
  }, []);

  // KAN-181: la atención de soporte vive en una conversación aparte; al
  // cerrarse (el usuario o el equipo) se vuelve a la conversación con la IA
  // que se estaba usando, con su sessionId y contexto intactos.
  const openSupportConversation = useCallback(() => {
    const current = conversationsRef.current.find((c) => c.id === activeIdRef.current);
    if (current?.supportOriginId) return; // ya estamos en la conversación de soporte
    const now = Date.now();
    const conv: ChatConversation = {
      id: createConversationId(),
      sessionId: createSessionId(),
      title: 'Atención al cliente',
      messages: [],
      createdAt: now,
      updatedAt: now,
      ...(current && current.messages.length > 0 ? { supportOriginId: current.id } : {}),
    };
    // Si la activa estaba vacía no se persiste (spec KAN-218 §4): se descarta.
    const base = current && current.messages.length === 0
      ? conversationsRef.current.filter((c) => c.id !== current.id)
      : conversationsRef.current;
    const next = addConversationWithEviction(base, conv);
    conversationsRef.current = next;
    setConversations(next);
    activeIdRef.current = conv.id;
    setActiveConversationId(conv.id);
  }, []);

  const returnToOriginConversation = useCallback(() => {
    const support = conversationsRef.current.find((c) => c.id === activeIdRef.current && c.supportOriginId)
      ?? conversationsRef.current.find((c) => c.supportOriginId);
    if (!support) return;
    const originId = support.supportOriginId;
    // La conversación de soporte queda como historial, pero ya no es "abierta".
    const next = conversationsRef.current.map((c) =>
      c.id === support.id ? { ...c, supportOriginId: undefined } : c
    );
    conversationsRef.current = next;
    setConversations(next);
    try {
      localStorage.setItem(CONVERSATIONS_STORAGE_KEY, JSON.stringify(next));
    } catch { /* best-effort */ }
    if (originId && next.some((c) => c.id === originId)) {
      activeIdRef.current = originId;
      setActiveConversationId(originId);
    }
  }, []);

  // Conecta (o reconecta) el socket del namespace /support y registra los
  // listeners que actualizan el slice soporte. Si se pasa sid, emite rejoin
  // para unirse a la sala del hilo (recovery al montar).
  const connectSupportSocket = useCallback(async (sid?: string) => {
    try {
      const sock = await supportSocket.connect();
      // Limpieza defensiva de listeners previos (re-conexión / recovery)
      sock.off('handoff:live');
      sock.off('handoff:queued');
      sock.off('handoff:outreach');
      sock.off('message');
      sock.off('typing');
      sock.off('closed');
      sock.on('handoff:live', (payload: { supportId?: string; adminName?: string } | undefined) => {
        if (payload?.supportId) {
          setSupportId(payload.supportId);
        }
        setSupportStatus('live');
        playSupportChime();
        const quien = payload?.adminName || 'el equipo';
        pushSystem(`✅ ${quien.charAt(0).toUpperCase() + quien.slice(1)} se conectó para atenderte. Escribí tu consulta abajo.`);
      });
      sock.on('handoff:queued', (payload: { supportId?: string } | undefined) => {
        if (payload?.supportId) {
          setSupportId(payload.supportId);
        }
        setSupportStatus('queued');
        pushSystem('✅ Tu mensaje fue enviado. Alguien del equipo lo va a ver y responder a la brevedad. Dejá más detalles abajo si querés.');
      });
      sock.on('handoff:outreach', (payload: { supportId?: string; status?: string; from?: string } | undefined) => {
        // Outreach proactivo: el equipo le escribió aunque no hubiera handoff.
        if (!payload?.supportId) return;
        restoredSupportIdRef.current = payload.supportId;
        setSupportId(payload.supportId);
        setSupportStatus(payload.status === 'live' ? 'live' : 'queued');
        sock.emit('rejoin', { supportId: payload.supportId });
        loadSupportHistoryRef.current?.(payload.supportId);
        playSupportChime();
        const quien = payload.from || 'el equipo';
        pushSystem(`🎧 ${quien.charAt(0).toUpperCase() + quien.slice(1)} te escribió desde atención al cliente. Respondé abajo.`);
        setIsOpen(true); // se le abre la ventana del chat directo
      });
      sock.on('message', (m: { from?: string; text?: string; ts?: string } | undefined) => {
        if (m?.from === 'agent' && m.text) {
          pushAgent(m.text, m.ts);
          playSupportChime();
        }
      });
      sock.on('typing', (t: { from?: string; isTyping?: boolean } | undefined) => {
        if (t?.from === 'agent') setSupportAdminTyping(!!t.isTyping);
      });
      sock.on('closed', () => {
        setSupportStatus('idle');
        setSupportId(null);
        setSupportAdminTyping(false);
        pushSystem('Fin de la atención. Podés seguir consultándome.');
        returnToOriginConversation();
        // NO desconectamos el socket: queda conectado pasivo (sala personal)
        // para recibir a tiempo real un outreach o el revive de este hilo si el
        // equipo vuelve a escribir — sin esperar a que el usuario recargue.
      });
      supportSocketRef.current = sock;
      if (sid) sock.emit('rejoin', { supportId: sid });
    } catch (e) {
      console.error('[Chat] Error al conectar socket de soporte:', e);
    }
  }, [pushAgent, pushSystem, returnToOriginConversation]);

  // Activa el modo soporte desde una señal del backend (la IA llamó
  // contactar_soporte y el stream manda el evento `handoff`): setea el hilo,
  // reconecta el socket y abre la ventana del chat. Vía ref para que el
  // handler del stream siempre use la versión fresca sin tocar sus deps.
  const activateSupportFromSignal = useCallback((h: { supportId?: string; status?: string }) => {
    if (!h?.supportId) return;
    openSupportConversation();
    restoredSupportIdRef.current = h.supportId;
    setSupportId(h.supportId);
    setSupportStatus(h.status === 'live' ? 'live' : 'queued');
    connectSupportSocket(h.supportId);
    setIsOpen(true);
    loadSupportHistoryRef.current?.(h.supportId);
    pushSystem(
      h.status === 'live'
        ? '🎧 Te estamos conectando con una persona del equipo…'
        : '🎧 Tu pedido quedó en la cola del equipo — te responden a la brevedad.'
    );
  }, [connectSupportSocket, pushSystem, openSupportConversation]);
  const activateSupportRef = useRef(activateSupportFromSignal);
  activateSupportRef.current = activateSupportFromSignal;

  // Quita mensajes duplicados por (role + content), quedándose con la primera
  // aparición. Se usa solo al restaurar el historial de soporte desde el server
  // para limpiar apilamientos (localStorage que ya tenía los mensajes + historial
  // del server + re-renders). No se aplica al chat con la IA cuando no hay
  // handoff activo, así que no afecta la conversación normal.
  const dedupeMessages = useCallback((msgs: ChatMessage[]): ChatMessage[] => {
    const seen = new Set<string>();
    return msgs.filter(m => {
      const key = `${m.role}::${m.content}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, []);

  // Carga el historial de un hilo de soporte (mensajes del equipo y del
  // usuario) y lo mergea al chat. Se usa al activar soporte por señal (tool
  // contactar_soporte) o por outreach proactivo, para que el mensaje que
  // originó el hilo quede visible.
  const loadSupportHistory = useCallback(async (supportId: string) => {
    try {
      const data = await ChatService.getHandoffHistory(supportId);
      const history = Array.isArray(data?.messages) ? data.messages : [];
      const historyMessages: ChatMessage[] = history
        .filter((m): m is { ts?: string; from?: string; text?: string } => !!m?.text)
        .map((m) => ({
          id: generateMessageId(),
          role: m.from === 'agent' ? 'agent' : 'user',
          content: m.text || '',
          timestamp: m.ts || new Date().toISOString(),
        }));
      if (!historyMessages.length) return;
      updateActiveMessages((msgs) => dedupeMessages([...msgs, ...historyMessages]));
    } catch {
      /* silencioso: el historial es best-effort */
    }
  }, [dedupeMessages, updateActiveMessages]);
  const loadSupportHistoryRef = useRef(loadSupportHistory);
  loadSupportHistoryRef.current = loadSupportHistory;

  // Recovery al montar / cambiar user: si hay handoff activo en el server
  // para esta identidad, restaurar supportId, status, historial y reconectar
  // el socket. Silencioso: si falla, no bloquea el chat IA.
  // Guard con restoredSupportIdRef: restaurar cada supportId una sola vez por
  // sesión para no apilar el historial, y dedup por (role+content) para limpiar
  // lo que ya vino de localStorage.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    ChatService.getActiveHandoff()
      .then(data => {
        if (cancelled) return;
        if (!data) {
          // Sin hilo activo: conectamos igual el socket /support para poder
          // recibir outreach proactivo del equipo (sala personal user:<id>).
          connectSupportSocket();
          return;
        }
        // Siempre reconectamos el socket (idempotente), pero el historial solo
        // se restaura una vez por supportId.
        if (restoredSupportIdRef.current !== data.supportId) {
          restoredSupportIdRef.current = data.supportId;
          const restoredStatus: SupportStatus = data.status === 'live' ? 'live' : 'queued';
          setSupportId(data.supportId);
          setSupportStatus(restoredStatus);
          type SupportHistoryMsg = { ts?: string; from?: string; fromName?: string; text?: string };
          const history: SupportHistoryMsg[] = Array.isArray(data.messages)
            ? (data.messages as SupportHistoryMsg[])
            : [];
          if (history.length > 0) {
            const historyMessages: ChatMessage[] = history.map((m) => ({
              id: generateMessageId(),
              role: m.from === 'agent' ? 'agent' : 'user',
              content: m.text || '',
              timestamp: m.ts || new Date().toISOString(),
            }));
            // Merge + dedup contra lo que ya está en la conversación activa
            // (p.ej. cargado de localStorage) para no duplicar.
            updateActiveMessages((msgs) => dedupeMessages([...msgs, ...historyMessages]));
          }
        }
        connectSupportSocket(data.supportId);
      })
      .catch(() => {
        /* silencioso: sin handoff activo o server offline */
      });
    return () => {
      cancelled = true;
    };
    // Intencionalmente sin connectSupportSocket/persistMessages/generateMessageId/dedupeMessages:
    // este efecto sólo debe correr cuando cambia `user`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Al abrir el chat con un hilo de soporte activo, refrescar el historial del
  // hilo: cubre los mensajes del equipo que llegaron mientras la ventana estuvo
  // cerrada (o la app desconectada) — lo primero que se ve es la conversación
  // completa y actualizada. Si NO hay hilo local, re-chequear contra el server:
  // el equipo pudo revivir o crear uno mientras estuvimos desconectados.
  useEffect(() => {
    if (!isOpen) return;
    if (supportId) {
      loadSupportHistoryRef.current?.(supportId);
      return;
    }
    ChatService.getActiveHandoff()
      .then(data => {
        if (data?.supportId) {
          activateSupportRef.current?.({ supportId: data.supportId, status: data.status });
        }
      })
      .catch(() => {
        /* silencioso: sin hilo activo */
      });
  }, [isOpen, supportId]);

  // Visitor: setea el estado de captura para que ChatWindow muestre el form
  // (SupportCaptureForm). El usuario logueado no pasa por acá: su botón
  // dispara startHandoff directo.
  const requestHandoffCapture = useCallback(() => {
    setSupportStatus('capture');
  }, []);

  const startHandoff = useCallback(async (
    initialMessage: string,
    visitorName?: string,
    visitorEmail?: string
  ) => {
    try {
      setSupportStatus('requesting');
      // El server valida initialMessage min(1); si viene vacío (flujo
      // logueado desde botón del header), mandamos un default corto.
      const initial = initialMessage.trim() || 'Hola, quiero hablar con una persona.';
      // Conversación nueva sin sessionId todavía: omitir el campo en vez de
      // mandar '' — el schema del server (min(1)) rechaza el string vacío con
      // 400 INVALID_INPUT (bug: botón de soporte en chat recién abierto).
      const originSessionId = getActiveSessionId();
      const { supportId: sid, status } = await ChatService.requestHandoff({
        initialMessage: initial,
        contextSummary: buildContextSummary(),
        ...(originSessionId ? { originSessionId } : {}),
        ...(visitorName ? { visitorName } : {}),
        ...(visitorEmail ? { visitorEmail } : {}),
      });
      setSupportId(sid);
      // Marcar este supportId como ya "contabilizado" localmente: el mensaje
      // inicial se pushó acá, así que un recovery posterior en la misma sesión
      // no debe volver a apilar el historial del server (el dedup igual lo
      // cubriría, pero esto lo evita de raíz).
      restoredSupportIdRef.current = sid;
      // Mostrar el mensaje inicial como mensaje user local (espejo del
      // texto que acaba de mandar al server). Si era el default placeholder,
      // también lo mostramos para que el usuario vea su "consulta inicial".
      openSupportConversation();
      pushUser(initial);
      await connectSupportSocket(sid);
      // El server emitirá handoff:live/handoff:queued por socket, pero
      // actualizamos acá también como fallback si el socket tarda o se cae.
      if (status === 'live') {
        setSupportStatus('live');
      } else if (status === 'queued') {
        setSupportStatus('queued');
      }
    } catch (e) {
      console.error('[Chat] Error al iniciar handoff:', e);
      setSupportStatus('idle');
      setError('No se pudo iniciar la atención.');
    }
  }, [buildContextSummary, getActiveSessionId, connectSupportSocket, pushUser, openSupportConversation]);

  const cancelHandoff = useCallback(() => {
    setSupportStatus('idle');
    setSupportAdminTyping(false);
  }, []);

  const sendSupportMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const sid = supportId;
    if (!sid) return;
    pushUser(trimmed);
    const sock = supportSocketRef.current;
    if (sock?.connected) {
      sock.emit('message', { supportId: sid, text: trimmed });
    } else {
      // Fallback REST si el socket no está conectado (reconexión en curso)
      await ChatService.sendHandoffMessage(sid, trimmed).catch(() => {});
    }
  }, [supportId, pushUser]);

  const closeHandoff = useCallback(async () => {
    const sid = supportId;
    if (!sid) return;
    const sock = supportSocketRef.current;
    if (sock?.connected) {
      sock.emit('close', { supportId: sid });
    } else {
      await ChatService.closeHandoff(sid).catch(() => {});
    }
    setSupportStatus('idle');
    setSupportId(null);
    setSupportAdminTyping(false);
    pushSystem('Cerraste la atención. Podés seguir consultándome.');
    returnToOriginConversation();
    // NO desconectamos el socket: queda conectado pasivo en la sala personal
    // para recibir EN VIVO el revive de este hilo (o un outreach) si el equipo
    // vuelve a escribir — sin esperar a que el usuario recargue la pantalla.
  }, [supportId, pushSystem, returnToOriginConversation]);

  // ─────────────────────────────────────────────────────────────────────
  // Cola offline + envío unificado (KAN-218)
  // ─────────────────────────────────────────────────────────────────────

  // Manejador del evento SSE ar_guide (extraído del viejo sendMessage inline).
  const handleARGuideEvent = useCallback((guideData: ARGuideEventData) => {
    console.log('[CHAT] AR Guide event received:', guideData);

    if (guideData.success === false) {
      console.warn('Guía falló:', guideData.error);
      if (guideData.textAnswer) {
        const errorMessage: ChatMessage = {
          id: generateMessageId(),
          role: 'assistant',
          content: guideData.textAnswer || 'Lo siento, no pude generar una guía visual.',
          timestamp: new Date().toISOString()
        };
        updateActiveMessages((msgs) => [...msgs, errorMessage]);
      }
    } else if (guideData.guideId && guideData.steps) {
      const guide: ARGuide = {
        guideId: guideData.guideId,
        taskId: guideData.taskId || '',
        steps: guideData.steps,
        currentStep: guideData.currentStep || 0,
        status: guideData.status || 'active'
      };
      // Preservar _source (golden/llm) en el guide object (ver GuideControls).
      if (guideData._source) {
        guide._source = guideData._source;
      }
      startGuide(guide);
      // El texto de la guía NO se agrega acá: el server lo manda también como
      // answer_delta y streamTurn ya lo escribe en la burbuja del turno. Agregarlo
      // otra vez duplicaba el mensaje (visto 2026-10-15).
    }
  }, [generateMessageId, updateActiveMessages, startGuide]);
  const handleARGuideEventRef = useRef(handleARGuideEvent);
  handleARGuideEventRef.current = handleARGuideEvent;

  // Drena la conversación dada: lotes de pendientes → un streamTurn por lote
  // (merge con \n\n + batchCount, spec KAN-218 §2/§5). Al reconectar se llama
  // por cada conversación con pendientes (drainAllConversations, updatedAt asc).
  const drainConversation = useCallback(async (conversationId: string) => {
    if (drainingRef.current || stopDrainRef.current) return;
    drainingRef.current = true;

    // Update scoped a la conversación del drain (puede no ser la activa).
    const updateConvMessages = (fn: (msgs: ChatMessage[]) => ChatMessage[]) => {
      const updated = conversationsRef.current.map((c) =>
        c.id === conversationId
          ? { ...c, messages: fn(c.messages).slice(-MAX_STORED_MESSAGES), updatedAt: Date.now() }
          : c
      );
      conversationsRef.current = updated;
      setConversations(updated);
      try {
        localStorage.setItem(CONVERSATIONS_STORAGE_KEY, JSON.stringify(updated));
      } catch { /* best-effort */ }
    };

    // Transcribe una nota de voz pendiente (KAN-225). true = lista para el
    // lote; false = frenar el drain de esta conversación (red, error, budget).
    const transcribePendingVoiceNote = async (messageId: string): Promise<boolean> => {
      const setVoiceNote = (patch: (m: ChatMessage) => ChatMessage) =>
        updateConvMessages((msgs) => msgs.map((m) => (m.id === messageId && m.voiceNote ? patch(m) : m)));

      setVoiceNote((m) => ({ ...m, voiceNote: { durationMs: m.voiceNote!.durationMs, transcribing: true } }));
      try {
        const stored = await voiceNoteStore.get(messageId);
        if (!stored) {
          throw Object.assign(new Error('La nota de voz ya no está disponible en este dispositivo.'), { code: 'VOICE_NOTE_MISSING' });
        }
        const { text, traceId } = await ChatService.transcribeVoiceNote(stored.blob, stored.mimeType, stored.durationMs);
        setVoiceNote((m) => ({
          ...m,
          content: text,
          voiceNote: { durationMs: m.voiceNote!.durationMs, ...(traceId ? { transcriptionTraceId: traceId } : {}) },
        }));

        // Conversación que arrancó con una nota: título con el texto real.
        const current = conversationsRef.current.find((c) => c.id === conversationId);
        if (current && current.title === VOICE_NOTE_CONVERSATION_TITLE) {
          const retitled = conversationsRef.current.map((c) =>
            c.id === conversationId ? { ...c, title: buildConversationTitle(text) } : c
          );
          conversationsRef.current = retitled;
          setConversations(retitled);
          try {
            localStorage.setItem(CONVERSATIONS_STORAGE_KEY, JSON.stringify(retitled));
          } catch { /* best-effort */ }
        }
        // El audio se conserva para poder escucharlo; se borra al cerrar la conversación.
        return true;
      } catch (err) {
        const code = (err as { code?: string })?.code;
        const message = err instanceof Error ? err.message : 'Error desconocido';

        // Budget: corta TODO el drain; la nota queda pendiente (spec §3).
        if (code === 'BUDGET_LIMIT_EXCEEDED' || message.includes('límite de uso gratuito')) {
          stopDrainRef.current = true;
          setBudgetLimitExceeded(true);
          setError(message);
          setVoiceNote((m) => ({ ...m, voiceNote: { durationMs: m.voiceNote!.durationMs } }));
          return false;
        }

        // Red caída: sigue pendiente para la próxima reconexión. Otro error
        // (vacía, server): nota en error; reintentar o descartar.
        const isNetworkError = code === 'CONNECTION_ERROR' || !isOnlineRef.current;
        setVoiceNote((m) => ({
          ...m,
          status: (isNetworkError ? 'pendiente' : 'error') as 'pendiente' | 'error',
          voiceNote: { durationMs: m.voiceNote!.durationMs },
        }));
        if (isNetworkError) {
          setError('Sin conexión — tus mensajes se enviarán automáticamente al reconectar.');
          setIsConnected(false);
        } else {
          setError('No se pudo entender la nota de voz. Podés reintentar o descartarla.');
        }
        return false;
      }
    };

    try {
      while (!stopDrainRef.current) {
        const conv = conversationsRef.current.find((c) => c.id === conversationId);
        if (!conv) break;

        // Fase de transcripción (KAN-225): las notas de voz pendientes se
        // transcriben en orden ANTES de armar lotes, así el lote respeta el
        // orden original y viaja todo como texto.
        const pendingVoice = conv.messages.find(
          (m) => m.role === 'user' && m.status === 'pendiente' && isUntranscribedVoiceNote(m)
        );
        if (pendingVoice) {
          const transcribed = await transcribePendingVoiceNote(pendingVoice.id);
          if (!transcribed) return;
          continue;
        }

        const batches = splitPendingIntoBatches(conv.messages);
        if (batches.length === 0) break;
        const batch = batches[0];
        const batchIds = new Set(batch.map((m) => m.id));

        // El request arranca → el lote pasa a enviado (spec §2).
        updateConvMessages((msgs) => msgs.map((m) =>
          batchIds.has(m.id) ? { ...m, status: 'enviado' as const } : m
        ));

        const assistantMessageId = generateMessageId();
        updateConvMessages((msgs) => [...msgs, {
          id: assistantMessageId,
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString(),
          isStreaming: true,
        }]);
        lastMessageIdRef.current = assistantMessageId;

        setIsStreaming(true);
        setIsConnected(true);
        streamingTTS.cancel();
        lastSpokenLengthRef.current = 0;
        abortControllerRef.current = new AbortController();

        let accumulated = '';
        try {
          await ChatService.streamTurn(
            {
              message: mergeBatchContents(batch),
              sessionId: conv.sessionId,
              batchCount: batch.length,
              ...buildOfflineTurnMeta(batch),
              // Contexto de pantalla del momento del envío. Un lote que estuvo en la
              // cola offline no lo lleva: la pantalla de ahora no es la de cuando se escribió.
              ...(batch.some((m) => m.queuedOffline)
                ? {}
                : { uiContext: await chatUi.getSnapshot().catch(() => undefined) }),
            },
            {
              onDelta: (text) => {
                accumulated += text;
                updateConvMessages((msgs) => msgs.map((m) =>
                  m.id === assistantMessageId ? { ...m, content: accumulated } : m
                ));
                // TTS streaming (mismo criterio que el viejo flujo inline)
                if (ttsMethodRef.current === 'elevenlabs' && isElevenLabsConfigured()) {
                  const nuevo = accumulated.slice(lastSpokenLengthRef.current);
                  if (nuevo.length > 5) {
                    lastSpokenLengthRef.current = accumulated.length;
                    streamingTTS.sendText(nuevo);
                  }
                }
              },
              onDone: ({ actions, traceId }) => {
                if (actions || traceId) {
                  updateConvMessages((msgs) => msgs.map((m) =>
                    m.id === assistantMessageId
                      ? { ...m, ...(actions ? { actions } : {}), ...(traceId ? { traceId } : {}) }
                      : m
                  ));
                }
              },
              onHandoff: (payload) => { activateSupportRef.current?.(payload); },
              onBudgetWarning: (payload) => {
                setBudgetWarning({
                  message: payload.message || 'Te estás acercando a tu límite de uso gratuito',
                  percentage: payload.percentage || 0,
                  remaining: payload.remaining || 0,
                  limit: payload.limit || 0
                });
              },
              onARGuide: (data) => { handleARGuideEventRef.current?.(data as ARGuideEventData); },
            },
            abortControllerRef.current.signal,
            // Guest (sin usuario logueado): sin Authorization → el server rutea a
            // visitorAgent (preguntas de la app/precios, sin operaciones).
            { isVisitor: !user }
          );

          updateConvMessages((msgs) => msgs.map((m) =>
            m.id === assistantMessageId ? { ...m, isStreaming: false } : m
          ));
          if (ttsMethodRef.current === 'elevenlabs' && isElevenLabsConfigured()) {
            streamingTTS.finalize();
          }
        } catch (err) {
          const code = (err as { code?: string })?.code;
          const message = err instanceof Error ? err.message : 'Error desconocido';

          // Abortado por el usuario: no tocar la cola.
          if (message.includes('abort')) {
            streamingTTS.cancel();
            return;
          }

          // Budget: terminal — lote en error, TODO el drain se corta y el
          // resto de los mensajes queda pendiente (spec §2).
          if (code === 'BUDGET_LIMIT_EXCEEDED' || message.includes('límite de uso gratuito')) {
            stopDrainRef.current = true;
            setBudgetLimitExceeded(true);
            setError(message || 'Llegaste a tu límite de uso gratuito. Escribile a soporte para ampliar tu plan.');
            updateConvMessages((msgs) => msgs
              .filter((m) => m.id !== assistantMessageId)
              .map((m) => (batchIds.has(m.id) ? { ...m, status: 'error' as const } : m)));
            return;
          }

          // Error de red (volvió a caer la conexión): el lote vuelve a
          // pendiente y se reintenta en la próxima reconexión (at-least-once).
          // Sesión expirada (AUTH_REQUIRED): igual — el lote queda pendiente y se
          // envía SOLO cuando el usuario vuelve a iniciar sesión (el drain corre
          // en cada reconexión/login). Así el chat nunca queda muerto.
          // Otro error de server: lote en error y frenamos esta conversación.
          const isAuthExpired = code === 'AUTH_REQUIRED';
          const isNetworkError = code === 'CONNECTION_ERROR' || !isOnlineRef.current;
          const requeue = isNetworkError || isAuthExpired;
          updateConvMessages((msgs) => msgs
            .filter((m) => m.id !== assistantMessageId)
            .map((m) => (batchIds.has(m.id)
              ? { ...m, status: (requeue ? 'pendiente' : 'error') as 'pendiente' | 'error' }
              : m)));
          setError(isAuthExpired
            ? 'Tu sesión expiró — iniciá sesión de nuevo. Tus mensajes quedan encolados y se envían solos al volver.'
            : isNetworkError
              ? 'Sin conexión — tus mensajes se enviarán automáticamente al reconectar.'
              : `Error al comunicarse con el asistente: ${message}`);
          setIsConnected(false);
          return;
        }
      }
    } finally {
      drainingRef.current = false;
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  }, [generateMessageId, chatUi]);

  // Drain de TODAS las conversaciones con pendientes, en orden de updatedAt.
  const drainAllConversations = useCallback(async () => {
    const conPendientes = conversationsRef.current
      .filter((c) => c.messages.some((m) => m.role === 'user' && m.status === 'pendiente'))
      .sort((a, b) => a.updatedAt - b.updatedAt);
    for (const conv of conPendientes) {
      if (stopDrainRef.current) break;
      await drainConversation(conv.id);
    }
  }, [drainConversation]);

  // Enviar mensaje: routing a soporte o al flujo IA con cola offline.
  const sendMessage = useCallback(async (content: string) => {
    // Routing a soporte: si hay handoff activo, mandamos por el canal de
    // soporte en vez del flujo IA. Cuando supportStatus === 'idle' (default)
    // este branch nunca ejecuta y el chat IA queda intocado.
    if (supportStatus === 'queued' || supportStatus === 'live') {
      await sendSupportMessage(content);
      return;
    }

    if (!content.trim() || isStreaming) {
      return;
    }

    setError(null);
    setIsConnected(true);

    // Outbox (spec §3): el mensaje entra al historial como pendiente ANTES de
    // intentar enviar. Sin conexión se queda ahí; online lo drena el debounce.
    const conv = ensureActiveConversation(content.trim());
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString(),
      status: 'pendiente',
      ...(isOnlineRef.current ? {} : { queuedOffline: true }),
    };
    updateActiveMessages((msgs) => [...msgs, userMessage]);

    if (!isOnlineRef.current) return; // encolado — el banner informativo lo muestra

    // Debounce 250ms: mensajes rápidos consecutivos se mergean en un turno.
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void drainConversation(conv.id);
    }, DEBOUNCE_MS);
  }, [supportStatus, sendSupportMessage, isStreaming, ensureActiveConversation, updateActiveMessages, generateMessageId, drainConversation]);

  // Nota de voz grabada sin conexión (KAN-225): el audio va a ChatAudioDB y a
  // la conversación entra un mensaje pendiente sin texto. Se transcribe y se
  // envía en el drain al reconectar (spec 2026-09-15-notas-de-voz-offline).
  const sendVoiceNote = useCallback(async (note: RecordedVoiceNote) => {
    setError(null);
    const conv = ensureActiveConversation(VOICE_NOTE_CONVERSATION_TITLE);
    const id = generateMessageId();
    try {
      await voiceNoteStore.save({
        id,
        conversationId: conv.id,
        blob: note.blob,
        mimeType: note.mimeType,
        durationMs: note.durationMs,
        createdAt: Date.now(),
      });
    } catch (e) {
      console.error('[Chat] No se pudo guardar la nota de voz:', e);
      setError('No se pudo guardar la nota de voz en este dispositivo.');
      return;
    }
    void voiceNoteStore.requestPersistence();

    const voiceMessage: ChatMessage = {
      id,
      role: 'user',
      content: '',
      timestamp: new Date().toISOString(),
      status: 'pendiente',
      voiceNote: { durationMs: note.durationMs },
      queuedOffline: true,
    };
    updateActiveMessages((msgs) => [...msgs, voiceMessage]);

    if (!isOnlineRef.current) return;
    // Volvió la señal mientras grababa: se drena como un mensaje más.
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void drainConversation(conv.id);
    }, DEBOUNCE_MS);
  }, [ensureActiveConversation, generateMessageId, updateActiveMessages, drainConversation]);

  // Descartar una nota de voz que todavía no se transcribió (× en la burbuja).
  const discardVoiceNote = useCallback(async (messageId: string) => {
    const conv = conversationsRef.current.find((c) => c.messages.some((m) => m.id === messageId));
    const target = conv?.messages.find((m) => m.id === messageId);
    if (!conv || !target?.voiceNote || target.voiceNote.transcribing || target.content) return;

    const updated = conversationsRef.current.map((c) =>
      c.id === conv.id
        ? { ...c, messages: c.messages.filter((m) => m.id !== messageId), updatedAt: Date.now() }
        : c
    );
    conversationsRef.current = updated;
    setConversations(updated);
    try {
      localStorage.setItem(CONVERSATIONS_STORAGE_KEY, JSON.stringify(updated));
    } catch { /* best-effort */ }
    setError(null);
    await voiceNoteStore.remove(messageId).catch(() => {});
  }, []);

  // Drain al reconectar (false→true) y al montar con conexión si quedó cola
  // de una sesión anterior sin conexión (spec KAN-218 §2).
  const prevOnlineRef = useRef(isOnline);
  useEffect(() => {
    if (!prevOnlineRef.current && isOnline) {
      stopDrainRef.current = false;
      void drainAllConversations();
    }
    prevOnlineRef.current = isOnline;
  }, [isOnline, drainAllConversations]);

  useEffect(() => {
    if (isOnline) {
      const hayPendientes = conversationsRef.current.some((c) =>
        c.messages.some((m) => m.role === 'user' && m.status === 'pendiente'));
      if (hayPendientes) void drainAllConversations();
    }
    // Solo al montar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drain al (re)iniciar sesión: si el lote quedó pendiente por AUTH_REQUIRED
  // (sesión expirada), se envía solo apenas el usuario vuelve a loguearse —
  // sin esto había que recargar la app para que la cola reviviera.
  const prevUserIdRef = useRef(user?.id);
  useEffect(() => {
    if (prevUserIdRef.current !== user?.id && user && isOnline) {
      stopDrainRef.current = false;
      void drainAllConversations();
    }
    prevUserIdRef.current = user?.id;
  }, [user, isOnline, drainAllConversations]);

  // Reintentar: los mensajes en error de la conversación activa vuelven a la
  // cola y se drenan ahora (KAN-218 — reemplaza el viejo reintento del último).
  const retryLastMessage = useCallback(async () => {
    const convId = activeIdRef.current;
    if (!convId) return;
    const conv = conversationsRef.current.find((c) => c.id === convId);
    if (!conv?.messages.some((m) => m.status === 'error')) return;

    const updated = conversationsRef.current.map((c) =>
      c.id === convId
        ? {
            ...c,
            messages: c.messages.map((m) => (m.status === 'error' ? { ...m, status: 'pendiente' as const } : m)),
            updatedAt: Date.now(),
          }
        : c
    );
    conversationsRef.current = updated;
    setConversations(updated);
    try {
      localStorage.setItem(CONVERSATIONS_STORAGE_KEY, JSON.stringify(updated));
    } catch { /* best-effort */ }
    setError(null);
    await drainConversation(convId);
  }, [drainConversation]);

  // Limpiar historial de la conversación activa
  const clearChat = useCallback(() => {
    setError(null);
    const convId = activeIdRef.current;
    if (convId) voiceNoteStore.removeByConversation(convId).catch(() => {}); // KAN-225
    updateActiveMessages(() => []);
  }, [updateActiveMessages]);

  // Nueva conversación: la activa pasa a ser una conversación vacía (no se
  // persiste hasta el primer mensaje, spec KAN-218 §4). Si la activa ya está
  // vacía, no hace nada.
  const createConversation = useCallback(() => {
    const current = conversationsRef.current.find((c) => c.id === activeIdRef.current);
    if (current && current.messages.length === 0) return;
    activeIdRef.current = null;
    setActiveConversationId(null);
  }, []);

  const selectConversation = useCallback((id: string) => {
    if (!conversationsRef.current.some((c) => c.id === id)) return;
    activeIdRef.current = id;
    setActiveConversationId(id);
  }, []);

  const closeConversation = useCallback((id: string) => {
    voiceNoteStore.removeByConversation(id).catch(() => {}); // audios de notas de voz (KAN-225)
    const remaining = conversationsRef.current.filter((c) => c.id !== id);
    conversationsRef.current = remaining;
    setConversations(remaining);
    try {
      localStorage.setItem(CONVERSATIONS_STORAGE_KEY, JSON.stringify(remaining));
    } catch { /* best-effort */ }
    if (activeIdRef.current === id) {
      const nextActive = remaining.length > 0
        ? remaining.reduce((a, b) => (a.updatedAt >= b.updatedAt ? a : b))
        : null;
      activeIdRef.current = nextActive?.id ?? null;
      setActiveConversationId(nextActive?.id ?? null);
    }
  }, []);

  // Toggle estado de abierto/cerrado
  const toggleChat = useCallback(() => {
    const newState = !isOpen;
    setIsOpen(newState);
    persistOpenState(newState);
  }, [isOpen, persistOpenState]);

  // Abrir chat
  const openChat = useCallback(() => {
    setIsOpen(true);
    persistOpenState(true);
  }, [persistOpenState]);

  // Cerrar chat
  const closeChat = useCallback(() => {
    setIsOpen(false);
    persistOpenState(false);
  }, [persistOpenState]);

  // Toggle modo de lectura automática
  const toggleAutoSpeak = useCallback(() => {
    setAutoSpeakEnabled(prev => {
      const newState = !prev;

      if (newState) {
        // Activando: Guardar timestamp actual para solo reproducir mensajes futuros
        setAutoSpeakActivatedAt(Date.now());
      } else {
        // Desactivando: detener reproducción Y limpiar timestamp
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
          window.speechSynthesis.cancel();
        }
        setAutoSpeakActivatedAt(null);
        setSpeakingMessageId(null);
      }

      return newState;
    });
  }, []);

  // Reproducir mensaje (controlado centralmente para evitar superposición)
  const speakMessage = useCallback(async (messageId: string, content: string) => {
    console.log('[Chat] speakMessage llamado - messageId:', messageId, 'ttsMethod:', ttsMethod);

    // Si ya hay otro mensaje reproduciéndose, cancelarlo primero
    if (speakingMessageId && speakingMessageId !== messageId) {
      console.log('[Chat] Cancelando mensaje anterior:', speakingMessageId);
      cancelSpeech();
    }

    // Verificar si streaming TTS está activo
    if (streamingTTS.isActive()) {
      console.log('[Chat] ⚠️ WARNING: streaming TTS está activo, cancelando...');
      streamingTTS.cancel();
    }

    setSpeakingMessageId(messageId);

    if (ttsMethod === 'elevenlabs' && isElevenLabsConfigured()) {
      try {
        console.log('[Chat] Usando voz ElevenLabs:', ELEVENLABS_VOICE_ID);

        // Cancelar audio anterior si existe
        if (currentAudioRef.current) {
          console.log('[Chat] Cancelando audio anterior de ElevenLabs');
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
          currentAudioRef.current = null;
        }

        const { audioUrl } = await elevenLabsTextToSpeech(content, ELEVENLABS_VOICE_ID);
        console.log('[Chat] Audio URL generada, comenzando reproducción...');

        const audio = new Audio(audioUrl);
        currentAudioRef.current = audio; // Guardar referencia

        audio.onended = () => {
          console.log('[Chat] Audio ElevenLabs terminado');
          setSpeakingMessageId(null);
          URL.revokeObjectURL(audioUrl); // Limpiar URL
          currentAudioRef.current = null; // Limpiar referencia
        };
        audio.onerror = (error) => {
          console.error('[Chat] ❌ ERROR en audio ElevenLabs:', error);
          setSpeakingMessageId(null);
          URL.revokeObjectURL(audioUrl);
          currentAudioRef.current = null; // Limpiar referencia
          // Fallback a nativo en caso de error
          speakNative(content);
        };

        audio.play();
      } catch (error) {
        console.error('Error con ElevenLabs, usando nativo:', error);
        setSpeakingMessageId(null);
        currentAudioRef.current = null; // Limpiar referencia en caso de error
        // Fallback a nativo en caso de error
        speakNative(content);
      }
    } else {
      // Usar Web Speech API nativo
      console.log('[Chat] Usando voz nativa del navegador');
      speakNative(content);
    }
  }, [speakingMessageId, ttsMethod]);

  // Función auxiliar para reproducción nativa
  const speakNative = useCallback((content: string) => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(content);

      // Intentar obtener una voz en español específica
      const voices = window.speechSynthesis.getVoices();

      // Buscar voces en español (prioridad: es-ES, es-MX, es-AR, etc.)
      const spanishVoice = voices.find(voice =>
        voice.lang.startsWith('es') &&
        (voice.lang.includes('ES') || voice.lang.includes('MX') || voice.lang.includes('AR'))
      ) || voices.find(voice => voice.lang.startsWith('es'));

      if (spanishVoice) {
        utterance.voice = spanishVoice;
        utterance.lang = spanishVoice.lang;
        console.log('[Chat] Usando voz nativa:', spanishVoice.name, 'lang:', spanishVoice.lang);
      } else {
        utterance.lang = 'es-ES';
        console.warn('[Chat] No se encontró voz en español, usando default es-ES');
      }

      utterance.onend = () => {
        setSpeakingMessageId(null);
      };

      utterance.onerror = () => {
        setSpeakingMessageId(null);
      };

      window.speechSynthesis.speak(utterance);
    } else {
      setSpeakingMessageId(null);
    }
  }, []);

  // Cancelar cualquier reproducción de audio
  const cancelSpeech = useCallback(() => {
    console.log('[Chat] cancelSpeech llamado');

    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      console.log('[Chat] Voz nativa cancelada');
    }
    // Cancelar streaming TTS de ElevenLabs
    if (streamingTTS.isActive()) {
      console.log('[Chat] Cancelando streaming TTS (estaba activo)');
    }
    streamingTTS.cancel();
    // Cancelar audio actual de ElevenLabs (no-streaming)
    if (currentAudioRef.current) {
      console.log('[Chat] Cancelando audio actual de ElevenLabs');
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    setSpeakingMessageId(null);
  }, []);

  // Establecer método de TTS
  const setTTSMethodWithPersist = useCallback((method: TTSMethod) => {
    console.log('[Chat] Cambiando método TTS de', ttsMethod, 'a', method);

    setTTSMethod(method);
    // Cancelar cualquier audio en progreso al cambiar método
    if (currentAudioRef.current) {
      console.log('[Chat] ⚠️ Cancelando audio actual al cambiar método');
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      console.log('[Chat] Voz nativa cancelada al cambiar método');
    }
    if (streamingTTS.isActive()) {
      console.log('[Chat] ⚠️ Streaming TTS estaba activo, cancelando al cambiar método');
    }
    streamingTTS.cancel();
    setSpeakingMessageId(null);
    try {
      localStorage.setItem(TTS_METHOD_KEY, method);
    } catch (e) {
      console.error('CHAT: Error persisting TTS method:', e);
    }
  }, [ttsMethod]);

  // Limpiar advertencia de presupuesto
  const clearBudgetWarning = useCallback(() => {
    setBudgetWarning(null);
  }, []);

  // Enviar feedback (thumbs up/down + comentario) al servidor y persistir en UI
  const submitFeedback = useCallback(async (
    messageId: string,
    feedbackThumb: boolean,
    feedbackText: string | null
  ) => {
    // Actualizar UI local inmediatamente
    updateActiveMessages((msgs) => msgs.map(msg =>
      msg.id === messageId
        ? { ...msg, feedbackThumb, feedbackText }
        : msg
    ));

    // Buscar el traceId para enviar al servidor
    const targetMessage = getActiveMessages().find(msg => msg.id === messageId);
    if (!targetMessage?.traceId) {
      console.warn('[Chat] No se puede enviar feedback al servidor: mensaje sin traceId');
      return;
    }

    try {
      await ChatService.submitFeedback(targetMessage.traceId, feedbackThumb, feedbackText);
    } catch (error) {
      // Mantener la actualización local aunque el servidor falle, para que el
      // usuario vea su elección reflejada en la UI.
      console.error('[Chat] Error al enviar feedback:', error);
    }
  }, [updateActiveMessages, getActiveMessages]);

  // Cleanup al desmontar
  useEffect(() => {
    // Listener para cambios en la configuración de chat habilitado
    const handleChatEnabledChange = (event: CustomEvent) => {
      setIsEnabled(event.detail);
    };

    window.addEventListener('chat-enabled-changed', handleChatEnabledChange as EventListener);

    return () => {
      // Cancelar requests pendientes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Limpiar timer del debounce de la cola offline (KAN-218)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Limpiar timeout de reconexión
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Cancelar streaming TTS
      streamingTTS.cancel();

      // Cancelar audio actual de ElevenLabs
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }

      // Desconectar socket de soporte (handoff)
      supportSocket.disconnect();
      supportSocketRef.current = null;

      // Remover listener
      window.removeEventListener('chat-enabled-changed', handleChatEnabledChange as EventListener);
    };
  }, []);

  const value: ChatContextType = {
    messages,
    conversations,
    activeConversationId,
    activeConversation,
    isOpen,
    isStreaming,
    isConnected,
    unreadCount,
    pendingCount,
    error,
    isEnabled,
    autoSpeakEnabled,
    autoSpeakActivatedAt,
    speakingMessageId,
    ttsMethod,
    budgetWarning,
    budgetLimitExceeded,
    // Slice soporte
    supportStatus,
    supportId,
    supportAdminTyping,
    sendMessage,
    sendVoiceNote,
    discardVoiceNote,
    clearChat,
    createConversation,
    selectConversation,
    closeConversation,
    toggleChat,
    openChat,
    closeChat,
    retryLastMessage,
    setError,
    toggleAutoSpeak,
    speakMessage,
    cancelSpeech,
    setTTSMethod: setTTSMethodWithPersist,
    clearBudgetWarning,
    submitFeedback,
    getActiveSessionId,
    // Acciones soporte
    requestHandoffCapture,
    startHandoff,
    cancelHandoff,
    sendSupportMessage,
    closeHandoff
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);

  if (context === undefined) {
    throw new Error('useChat debe ser usado dentro de un ChatProvider');
  }

  return context;
};
